import React, { FormEvent } from 'react';
import Highcharts from 'highcharts';
import HighchartsAccessibility from 'highcharts/modules/accessibility';
import { NumberFormatValues, SourceInfo } from 'react-number-format';
import DisclosureWithSymbol from '@ecs/tng-common/components/disclosure-with-symbol/DisclosureWithSymbol';
import { addCommas, scrollTo, pluralize } from '@ecs/tng-common/scripts/helpers';
import { TabPane, Tabs } from 'react-bootstrap';
// import { Link } from 'gatsby';
import { getAllParams } from '../../helpers/functions';
import {
  CalculatorProps,
  drawHighChart,
  earningOnFutureContribution,
  interestOnDeposit
} from '../../helpers/calculators';
import { calcEducationalData } from '../../content/documents/disclosures';
import FormattedInput from '../FormattedInput';

type compFreq = 'Daily' | 'Monthly' | 'Quarterly' | 'Semiannually' | 'Annually';
type inputTypes = 'deposit' | 'contribution' | 'apy' | 'period';
type calculationsType = {
  period: number;
  apyPercentage: number;
  contribution: number;
  totalSavings: number;
  totalInvestment: number;
  totalInterestEarned: number;
  compFreqN: number;
  deposit: number;
  apy: number;
};
type SavingsCalcState = {
  deposit: number;
  contribution: number;
  apy: number;
  compFreq: compFreq;
  period: number;
  calculations: calculationsType;

  errors: {
    deposit: string;
    contribution: string;
    apy: string;
    period: string;
  };
};

const config: Record<inputTypes, any> & {
  title: string;
  tableTabTitle1: string;
  tableTabTitle2: string;
  highChartTitle: string;
  highChartLegendName1: string;
  highChartLegendName2: string;
  tableTh: string[];
  compFreq: any;
} = {
  title: 'Savings calculator',
  tableTabTitle1: 'Total earnings',
  tableTabTitle2: 'Interest schedule',
  highChartTitle: 'Total savings',
  highChartLegendName1: 'Total contributions',
  highChartLegendName2: 'Total interest earned',
  tableTh: ['Year', 'Total Savings', 'Contributions', 'Interest earned'],
  deposit: {
    id: 'deposit',
    name: 'Initial deposit',
    type: 'tel',
    allowNegative: false,
    maxLength: 9,
    thousandSeparator: ',',
    required: true,
    prefix: '$ ',
    isAllowed: (values: NumberFormatValues) => {
      const { floatValue } = values;
      return floatValue === undefined || (floatValue <= 250000 && floatValue > 0);
    },
    allowLeadingZeros: false,
    errorMessages: {
      required: 'Please enter a valid deposit value up to $250,000'
    }
  },
  apy: {
    id: 'apy',
    name: 'APY',
    type: 'tel',
    allowNegative: false,
    suffix: ' %',
    isAllowed: (values: NumberFormatValues) => {
      const { floatValue } = values;
      return floatValue === undefined || (floatValue <= 10 && floatValue >= 0.1);
    },
    required: true,
    allowLeadingZeros: false,
    errorMessages: {
      required: 'Enter a valid APY: 0.1%–10%'
    }
  },
  contribution: {
    id: 'contribution',
    name: 'Monthly contribution',
    type: 'tel',
    allowNegative: false,
    maxLength: 9,
    required: false,
    thousandSeparator: ',',
    prefix: '$ ',
    isAllowed: (values: NumberFormatValues) => {
      const { floatValue, value } = values;
      // Allow if floatValue is undefined or less than or equal to 100000
      if (floatValue === undefined || floatValue <= 100000) {
        // Check for leading zeros
        const inputValue = value || '';
        // Allow if it doesn't start with multiple zeros
        return !/^0{2,}/.test(inputValue);
      }
      return false; // Disallow if above 100000
    },
    allowLeadingZeros: false,
    errorMessages: {
      required: 'Enter a valid monthly contribution up to $100,000'
    }
  },
  period: {
    id: 'period',
    name: 'Investment period',
    type: 'tel',
    allowNegative: false,
    maxLength: 8,
    required: true,
    suffix: ' years',
    isAllowed: (values: NumberFormatValues) => {
      const { floatValue } = values;
      return floatValue === undefined || (floatValue <= 30 && floatValue >= 1);
    },
    allowLeadingZeros: false,
    errorMessages: {
      required: 'Enter an investment period between 1 and 30'
    }
  },
  compFreq: {
    id: 'compFreq',
    name: 'Compound frequency',
    type: 'select',
    options: ['Daily', 'Monthly', 'Quarterly', 'Semiannually', 'Annually'],
    required: true
  }
};

export default class SavingsCalc extends React.Component<CalculatorProps, SavingsCalcState> {
  constructor(props: CalculatorProps) {
    super(props);
    this.state = {
      deposit: 10000,
      contribution: 200,
      period: 10,
      apy: 2.5,
      compFreq: 'Monthly',
      calculations: {} as calculationsType,
      errors: {
        deposit: '',
        contribution: '',
        apy: '',
        period: ''
      }
    };
  }

  componentDidMount() {
    if (this.props.isFull && typeof window !== 'undefined') {
      const queryParams = getAllParams();
      if (queryParams) {
        const { deposit, period, apy, contribution } = queryParams;
        this.setState(
          (prevState) => ({
            deposit: deposit ? Number(deposit) : prevState.deposit,
            period: period ? Number(period) : prevState.period,
            apy: apy ? Number(apy) : prevState.apy,
            contribution: contribution ? Number(contribution) : prevState.contribution
          }),
          () => {
            this.calculate(false);
          }
        );
      }
    }
    HighchartsAccessibility(Highcharts);
  }

  handleChange = (sourceInfo: SourceInfo, values: NumberFormatValues) => {
    const { event } = sourceInfo;
    if (!event) return;
    const target = event.target as HTMLInputElement;
    const name = target.name as inputTypes;
    const { errors } = this.state;
    let floatValue = Number(values?.floatValue);
    if (!config[name].required && !floatValue) {
      floatValue = 0;
    }

    errors[name] =
      (!floatValue || Number.isNaN(floatValue) || Number(floatValue) <= 0) && config[name].required
        ? config[name].errorMessages.required
        : '';

    this.setState({
      errors,
      [name]: floatValue
    } as unknown as Pick<SavingsCalcState, keyof SavingsCalcState>);
  };

  handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!this.checkIfFormIsInvalid()) {
      this.calculate(true);
    }
  };

  getCompFreq(compFreq: compFreq) {
    const frequencyMap: Record<string, number> = {
      Daily: 365,
      Monthly: 12,
      Quarterly: 4,
      Semiannually: 2,
      Annually: 1
    };

    return frequencyMap[compFreq] || 12; // Default to 12 if compFreq is not found
  }

  calculate(toScroll?: boolean) {
    const { deposit, apy, period, compFreq, contribution } = this.state;
    const compFreqN = this.getCompFreq(compFreq);
    const totalInvestment = deposit + contribution * 12 * period;
    const apyPercentage = apy / 100;
    const totalSavings =
      interestOnDeposit(deposit, apyPercentage, period, compFreqN) +
      earningOnFutureContribution(contribution, apyPercentage, period, compFreqN);
    const totalInterestEarned = totalSavings - totalInvestment;
    this.setState(
      {
        calculations: {
          compFreqN,
          totalSavings,
          totalInvestment,
          totalInterestEarned,
          period,
          contribution,
          apyPercentage,
          deposit,
          apy
        }
      },
      () => {
        if (typeof toScroll === 'undefined' || toScroll) {
          scrollTo('calcTotals', -24);
        }
      }
    );
  }

  addQueryParams(): string {
    const query: { [key: string]: string } = {};
    const { period } = this.state;

    if (period) {
      query.term = period.toString();
    }

    return new URLSearchParams(query).toString();
  }

  checkIfFormIsInvalid() {
    const { errors } = this.state;
    return Object.values(errors).some((error) => error.trim() !== '');
  }

  render() {
    const { isFull } = this.props;
    const { deposit, errors, contribution, apy, period, compFreq, calculations } = this.state;

    const renderForm = () => {
      return (
        <form
          className={`${calculations ? 'mb-4' : 'content-mb'} border rouded rounded-3 p-3 p-lg-4 ${!isFull ? 'rounded-top-0' : ''}`}
          onSubmit={this.handleSubmit}>
          <div
            className={`col-sm-10 col-md-8 ${isFull ? 'col-lg-12 col-xl-12' : 'col-lg-9 col-xl-8'}`}>
            <div className="mb-3 form-floating">
              <FormattedInput
                config={config.deposit}
                value={deposit?.toString()}
                errors={errors.deposit}
                onValueChange={(values: NumberFormatValues, sourceInfo: SourceInfo) =>
                  this.handleChange(sourceInfo, values)
                }
                placeholder={deposit?.toString()}
              />
              {errors.deposit && <div className="invalid-feedback">{errors.deposit}</div>}
            </div>
            <div className="mb-3 form-floating">
              <FormattedInput
                config={config.contribution}
                value={contribution?.toString()}
                errors={errors.contribution}
                onValueChange={(values: NumberFormatValues, sourceInfo: SourceInfo) =>
                  this.handleChange(sourceInfo, values)
                }
                placeholder={contribution?.toString()}
              />
              {errors.contribution && <div className="invalid-feedback">{errors.contribution}</div>}
            </div>
            <div className="mb-3 form-floating">
              <FormattedInput
                config={config.apy}
                value={apy?.toString()}
                errors={errors.apy}
                onValueChange={(values: NumberFormatValues, sourceInfo: SourceInfo) =>
                  this.handleChange(sourceInfo, values)
                }
                placeholder={apy?.toString()}
              />
              {errors.apy && <div className="invalid-feedback">{errors.apy}</div>}
            </div>
            <div className="mb-3 form-floating">
              <select
                className="form-select"
                onChange={(e) => {
                  this.setState({
                    compFreq: e.target.value as compFreq
                  });
                }}
                id={config.compFreq.id}
                name={config.compFreq.id}
                value={compFreq}>
                {config.compFreq.options.map((option: string) => (
                  <option
                    key={option}
                    value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <label
                className="input-label"
                htmlFor={config.compFreq.id}>
                {config.compFreq.name}
              </label>
            </div>
            <div className="mb-3 form-floating">
              <FormattedInput
                config={config.period}
                value={period?.toString()}
                errors={errors.period}
                onValueChange={(values: NumberFormatValues, sourceInfo: SourceInfo) =>
                  this.handleChange(sourceInfo, values)
                }
                placeholder={period?.toString()}
              />
              {errors.period && <div className="invalid-feedback">{errors.period}</div>}
            </div>
            <button
              disabled={this.checkIfFormIsInvalid()}
              type="submit"
              className="btn btn-secondary mt-3">
              Calculate
            </button>
          </div>
        </form>
      );
    };
    const renderTable = () => {
      const paymentsArray = Array.from({ length: calculations.period }, (_, index) => index + 1);
      const contributionYearly = calculations.contribution * 12;
      let contributionCalc = calculations.deposit;

      return (
        <table className="table table-striped text-break">
          <thead>
            <tr>
              {config.tableTh.map((th) => (
                <th key={th}>{th}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paymentsArray.map((year) => {
              contributionCalc += contributionYearly;
              const totalSavings =
                interestOnDeposit(
                  calculations.deposit,
                  calculations.apyPercentage,
                  year,
                  calculations.compFreqN
                ) +
                earningOnFutureContribution(
                  contribution,
                  calculations.apyPercentage,
                  year,
                  calculations.compFreqN
                );
              const totalInterestEarned = totalSavings - contributionCalc;
              return (
                <tr key={year}>
                  <td>{year}</td>
                  <td>${addCommas(totalSavings.toFixed(0))}</td>
                  <td>${addCommas(contributionCalc.toFixed(0))}</td>
                  <td>${addCommas(totalInterestEarned.toFixed(0))}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      );
    };
    const renderTotals = () => {
      return (
        <div className={`row gx-md-6 ${isFull ? 'pt-4 gy-4' : 'pt-3 pt-lg-4 gy-3 gy-lg-4'}`}>
          <div className="col-md-6 col-lg-12 col-xl-6">
            <div className="bg-light rounded-3 p-4 d-flex flex-wrap text-center mb-5">
              <span className="text-gray w-100">
                Total savings<sup>†</sup> in {period} year{pluralize(period)}:
              </span>
              <span className="h2 mb-0 w-100">
                ${addCommas(calculations?.totalSavings?.toFixed(0))}
              </span>
            </div>
            <div className="border-bottom d-flex justify-content-between pb-2 pb-lg-3">
              <span className="text-gray">Initial deposit:</span>
              <span className="fw-bold">${addCommas(calculations.deposit.toFixed(2))}</span>
            </div>
            <div className="border-bottom d-flex justify-content-between py-2 py-lg-3">
              <span className="text-gray">Monthly contribution:</span>
              <span className="fw-bold">${addCommas(calculations.contribution?.toFixed(2))}</span>
            </div>
            <div className="border-bottom d-flex justify-content-between py-2 py-lg-3">
              <span className="text-gray">APY:</span>
              <span className="fw-bold">{calculations.apy}%</span>
            </div>
            <div className="border-bottom d-flex justify-content-between py-2 py-lg-3">
              <span className="text-gray">Total investment:</span>
              <span className="fw-bold">${addCommas(calculations.totalInvestment.toFixed(2))}</span>
            </div>
            <div className="d-flex justify-content-between pt-2 pt-lg-3">
              <span className="text-gray">Total interest earned:</span>
              <span className="fw-bold">
                ${addCommas(calculations.totalInterestEarned.toFixed(2))}
              </span>
            </div>
          </div>
          {drawHighChart({
            title: config.highChartTitle,
            option1: {
              name: config.highChartLegendName1,
              value: parseFloat(
                ((calculations.totalInvestment / calculations.totalSavings) * 100).toFixed(2)
              )
            },
            option2: {
              name: config.highChartLegendName2,
              value: parseFloat(
                ((calculations.totalInterestEarned / calculations.totalSavings) * 100).toFixed(2)
              )
            }
          })}
        </div>
      );
    };
    const renderCalculationsBox = () => {
      return Object.keys(calculations).length > 0 ? (
        <>
          <div
            id="calcTotals"
            className="result border rounded-3 overflow-hidden body-text-sm">
            <div className={`px-3 pb-3 pb-lg-4 px-lg-4 ${isFull ? 'pt-3 pt-lg-4' : ''}`}>
              {isFull ? (
                <Tabs
                  id="calc-tab"
                  defaultActiveKey="calcTab1"
                  className="nav-underline ">
                  <TabPane
                    eventKey="calcTab1"
                    title={config.tableTabTitle1}>
                    {renderTotals()}
                  </TabPane>
                  <TabPane
                    eventKey="calcTab2"
                    title={config.tableTabTitle2}>
                    {renderTable()}
                  </TabPane>
                </Tabs>
              ) : (
                renderTotals()
              )}
            </div>
            <div className="bg-light p-3 p-lg-4 disclosures text-gray">
              <DisclosureWithSymbol disclosure={calcEducationalData} />
            </div>
          </div>
          {/* // REPLACE WITH GATSBY LINK */}
          {!isFull && (
            <p className="body-text-sm mt-3 d-flex align-items-center mb-6 mb-lg-7">
              <span className="ecs-font-icon-con-calculator font-icon-size-md" /> Try the full&nbsp;
              <a href={`/blogs/ask-experian/savings-calculator/?${this.addQueryParams()}`}>
                savings calculator
              </a>
              &nbsp;with more features.
            </p>
          )}
        </>
      ) : null;
    };
    return isFull ? (
      <div className="d-lg-flex row content-mt">
        <div className="col-lg-5 col-xl-4">{renderForm()}</div>
        <div className="col-lg-7 col-xl-8">{renderCalculationsBox()}</div>
      </div>
    ) : (
      <>
        <h4 className="calc-title mb-0 rounded-3 rounded-bottom-0 p-3 py-lg-4 px-lg-5 bg-midnight-blue text-white content-mt">
          {config.title}
        </h4>
        {renderForm()}
        {renderCalculationsBox()}
      </>
    );
  }
}
